import cs from './cs.json';
import * as Sentry from '@sentry/browser';

export const localize = () => {
	return cs;
};

export const populate = (translation: string, replacements: Record<string, string | number>) => {
	let selectedString = translation;
	for (const key in replacements) {
		// @ts-expect-error Replace does take a number as an argument (well, it does not, but it gets automatically converted to a string inside the function anyway...)
		selectedString = selectedString.replace(`{{${key}}}`, replacements[key]);
	}

	return selectedString;
};

/**
 * Pluralize a string based on count. The translation array should have between 2 and 4 elements:
 * 1. Singular
 * 2. Plural for 2-4
 * 3. Plural for 5+
 * 4. [optional] Fallback text when the count is 0
 *
 * Example usage:
 * ```
 * pluralize(4, ['{{count}} auto', '{{count}} auta', '{{count}} aut', 'Žádná auta'], true) // => '4 auta'
 * pluralize(4, ['{{count}} auto značky {{brand}}', '{{count}} auta značky {{brand}}', '{{count}} aut značky {{brand}}', 'Žádná auta značky {{brand}}'], { brand: 'Škoda', count: 4 }) // => '4 auta značky Škoda'
 * ```
 * @param count The number to base the pluralization on
 * @param translations The array of translations, requires 4 elements
 * @param replacements Optional replacements for the string, either a `true` to automatically replace the `{{count}}` with the input count or an object with key-value pairs relevant to the translations
 */
export const pluralize = (
	count: number,
	translations: string[],
	replacements: Record<string, string | number> | boolean,
) => {
	let selectedString: string | undefined = undefined;

	if (count === 1) selectedString = translations[0];
	else if (count >= 2 && count <= 4) selectedString = translations[1];
	else if (count >= 5) selectedString = translations[2];
	else selectedString = translations[3];

	if (selectedString === undefined) {
		if (import.meta.env.DEV) {
			throw new Error(
				`Pluralize has no valid string for count: ${count}. Options were: ${translations.join(', ')}. Replacements: ${JSON.stringify(
					replacements,
				)}`,
			);
		} else {
			Sentry.captureException(
				new Error(
					`Pluralize has no valid string for count: ${count}. Options were: ${translations.join(', ')}. Replacements: ${JSON.stringify(
						replacements,
					)}`,
				),
			);
			// better to return this string than to crash the app
			return 'Missing text';
		}
	}

	if (replacements) {
		return populate(selectedString, replacements === true ? { count } : replacements);
	}

	return selectedString;
};
